import React, { useState, useRef, useId } from "react";
import { cn } from "../../lib/cn";

/**
 * Tooltip - dark bg, white text, appears on hover/focus with a short delay.
 * @param {string} content
 * @param {"top"|"bottom"|"left"|"right"} [side="top"]
 * @param {React.ReactNode} children - a single focusable/hoverable element
 * @param {number} [delay=300]
 */
export function Tooltip({ content, side = "top", children, delay = 300 }) {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);
  const tooltipId = useId();

  function show() {
    timeoutRef.current = setTimeout(() => setIsVisible(true), delay);
  }

  function hide() {
    clearTimeout(timeoutRef.current);
    setIsVisible(false);
  }

  const sideClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {React.cloneElement(children, { "aria-describedby": isVisible ? tooltipId : undefined })}
      {isVisible && (
        <span
          role="tooltip"
          id={tooltipId}
          className={cn(
            "pointer-events-none absolute z-20 whitespace-nowrap rounded bg-neutral-900 px-2 py-1 text-xs text-white shadow-popover dark:shadow-black/40",
            sideClasses[side]
          )}
        >
          {content}
        </span>
      )}
    </span>
  );
}
